import join from 'url-join';

import { asyncAction, createAction, http } from '../util/index';
import { formatSearchParams, formatFacetParams } from '../util/formatSearchParams';
import { validateCart } from '../carts/actionCreators';

import { AVAILABILITY_GROUP_SIZE, ORDER_TYPE_ATONCE } from './constants';

// [500] ECOMM TIMEOUT ERROR CODE CHECK
const QUICK_ADD_COUNT = 500;

const { VITE_API_URL: apiUrl, VITE_API2_URL: api2Url } = import.meta.env;
const api = join(apiUrl, 'v1');
const api2 = join(api2Url, 'v1');

const productServiceFacetValues = [
  'catalogs',
  'category',
  'color',
  'dimensions',
  'division',
  'gender',
  'segment',
  'silhouette',
  'silhouetteType',
  'size',
  'sport',
  'team'
];

export const fetchDepartmentsByAccountId = asyncAction({
  baseName: 'REQUEST_DEPARTMENTS_BY_ACC_ID',
  action: action => accountId => dispatch => {
    dispatch(action.start(accountId));

    return http
      .get(join(api2, `/departments?filter=accountId(${accountId})&count=10000`))
      .then(({ data: { objects } }) => {
        dispatch(action.success(objects));

        return objects;
      })
      .catch(error => dispatch(action.error({ error })));
  }
});

export const selectShopDepartment = createAction('SELECT_SHOP_DEPARTMENT_LEGACY', payload => {
  localStorage.setItem('selectedDepartment', JSON.stringify(payload));
  return payload;
});

export const requestProductDetailsV2 = asyncAction({
  baseName: 'REQUEST_PRODUCT_DETAILS_V2',
  action: action => values => dispatch => {
    dispatch(action.start(values));
    const { accountNumber, deptNumber, styleNumber, language, cartType } = values;
    return http
      .get(
        join(
          api2,
          'products',
          `?filter=accountNumber(${accountNumber})`,
          `&filter=departmentNumber(${deptNumber})`,
          `&filter=style(${styleNumber})`,
          `&filter=languageCodes(${language})`,
          `&filter=cartType(${cartType})`,
          '&filter=realTime(true)'
        )
      )
      .then(({ data: response }) => dispatch(action.success(response.objects)))
      .catch(error => dispatch(action.error({ error })));
  }
});

export const requestProductDetails = asyncAction({
  baseName: 'REQUEST_PRODUCT_DETAILS_LEGACY',
  action: action => values => dispatch => {
    dispatch(action.start(values));
    const {
      accountNumber,
      deptNumber,
      includeUnavailableProduct,
      orderType,
      pricelistType,
      region,
      styleNumber,
      language
    } = values;
    const inclUnavailParam =
      includeUnavailableProduct && orderType === ORDER_TYPE_ATONCE ? '&includeUnavailableProduct=true' : '';
    return http
      .get(
        join(
          api,
          `accounts/${accountNumber}/departments/${deptNumber}/catalogs/${orderType}`,
          `?facets=${productServiceFacetValues.join(',')}`,
          `&styleNumbers=${styleNumber}`,
          `&region=${region}`,
          `&pricelistType=${pricelistType}`,
          `&language=${language}${inclUnavailParam}`
        )
      )
      .then(({ data: response }) => dispatch(action.success(response)))
      .catch(error => dispatch(action.error({ error })));
  }
});

export const requestProductsById = asyncAction({
  baseName: 'REQUEST_PRODUCTS_BY_ID_LEGACY',
  action: action => values => dispatch => {
    dispatch(action.start(values));
    const { accountNumber, deptNumber, language, orderType, region, pricelistType, productIds } = values;
    return http
      .get(
        join(
          api,
          `accounts/${accountNumber}/departments/${deptNumber}/catalogs/${orderType}`,
          `?count=${QUICK_ADD_COUNT}`,
          `&productCode=${productIds.join(',')}`,
          `&region=${region}`,
          `&pricelistType=${pricelistType}`,
          `&language=${language}`
        )
      )
      .then(({ data: response }) => dispatch(action.success(response)))
      .catch(error => dispatch(action.error({ error })));
  }
});

export const cacheProductsRequestData = createAction('CACHE_PRODUCTS_REQUEST_LEGACY');
export const requestProducts = asyncAction({
  baseName: 'REQUEST_PRODUCTS_LEGACY',
  action: action => ({
    accountNumber,
    after,
    count,
    deptNumber,
    isFiltered,
    language,
    orderType,
    pricelistType,
    productCode,
    productName,
    region,
    selectedFacets,
    selectedFilters,
    sortDirection,
    sortField,
    styleNumbers,
    wholesaleMax,
    wholesaleMin,
    allSizesAvailable,
    includeUnavailableProduct,
    sizeLocale,
    colorCode
  }) => dispatch => {
    dispatch(action.start({ selectedFilters }));

    const productQuery = [after ? `&after=${after}` : ''];
    const aggregationValues = ['wholesale_min', 'wholesale_max'];
    const facetFilterSearch = [
      formatFacetParams(selectedFacets),
      formatSearchParams(selectedFilters),
      productCode ? `&productCode=${productCode}` : '',
      productName ? `&productName=${productName}` : '',
      styleNumbers ? `&styleNumbers=${styleNumbers}` : '',
      wholesaleMin ? `&wholesaleMin=${wholesaleMin}` : '',
      wholesaleMax ? `&wholesaleMax=${wholesaleMax}` : '',
      sortField ? `&sortField=${sortField}` : '',
      sortDirection ? `&sortDirection=${sortDirection}` : '',
      allSizesAvailable ? `&allSizesAvailable=${allSizesAvailable}` : '',
      includeUnavailableProduct ? `&includeUnavailableProduct=${includeUnavailableProduct}` : '',
      selectedFacets.division === 'FTWR' && selectedFacets.gender ? `&sizeLocale=${sizeLocale}` : '',
      colorCode ? `&colorCode=${colorCode}` : ''
    ];

    const req = join(
      `${productQuery.join('')}`,
      `&aggregations=${aggregationValues.join(',')}`,
      `&facets=${productServiceFacetValues.join(',')}${facetFilterSearch.join('')}`,
      `&region=${region}&pricelistType=${pricelistType}&language=${language}`
    );

    dispatch(
      cacheProductsRequestData(
        join(`?${req.substr(1)}`, `&deptNumber=${deptNumber}&accountNumber=${accountNumber}&orderType=${orderType}`)
      )
    );

    return http
      .get(join(api, `accounts/${accountNumber}/departments/${deptNumber}/catalogs/${orderType}?count=${count}`, req))
      .then(({ data: response }) => {
        return dispatch(
          action.success({
            ...response,
            dept: deptNumber,
            updateFilters: isFiltered,
            hasUnavailableProducts: includeUnavailableProduct
          })
        );
      })
      .catch(error => dispatch(action.error({ error })));
  }
});

export const clearProducts = createAction('CLEAR_PRODUCTS_LEGACY');
export const selectProduct = createAction('SELECT_PRODUCT_LEGACY');
export const resetCursor = createAction('CLEAR_CURSOR_LEGACY');
export const selectFacet = createAction('SELECT_FACET_LEGACY');
export const clearSelectedFacet = createAction('CLEAR_SUBTYPE_LEGACY');
export const storeActiveSubTypes = createAction('STORE_ACTIVE_SUBTYPES_LEGACY');
export const storeAllSubTypes = createAction('STORE_ALL_SUBTYPES_LEGACY');

export const setSearchTerm = createAction('SET_PRODUCT_SEARCH_LEGACY');

export const addSelectedFilter = createAction('ADD_SELECTED_FILTER_LEGACY');
export const removeSelectedFilter = createAction('REMOVE_SELECTED_FILTER_LEGACY');
export const addPriceRange = createAction('ADD_PRICE_RANGE_LEGACY');
export const togglePriceRangeSet = createAction('TOGGLE_PRICE_RANGE_SET_LEGACY');

export const clearSelectedFiltersMinusCatalogs = createAction('CLEAR_SELECTED_FILTERS_MINUS_CATALOGS_LEGACY');
export const clearSelectedFilters = createAction('CLEAR_SELECTED_FILTERS_LEGACY');

export const clearFacets = createAction('CLEAR_FACETS_LEGACY');

export const clearAvailability = createAction('CLEAR_PRODUCT_AVAILABILITY');

export const setAllSizesAvailable = createAction('SET_ALL_SIZES_AVAILABLE');
export const setIncludeUnavailableProduct = createAction('SET_INCLUDE_UNAVAILABLE_PRODUCT');

/**
 * @name requestAvailability
 * @async
 * @desc request availability for cart products
 * @usage cart, checkout order review, pdp
 * @param {object} params
 * @param {string} accountNumber account number for product availability
 * @param {object} productCodes product codes for availability
 */
export const requestAvailability = asyncAction({
  baseName: 'REQUEST_PRODUCT_AVAILABILITY',
  action: action => (values, realTime = false) => dispatch => {
    dispatch(action.start(values));

    const { accountNumber, departmentNumber, productCodes } = values;
    const uniqueProductCodes = [...new Set(productCodes)];
    const groupedProductCodes = [];

    for (let i = 0; i < uniqueProductCodes.length; i += AVAILABILITY_GROUP_SIZE) {
      groupedProductCodes.push(uniqueProductCodes.slice(i, i + AVAILABILITY_GROUP_SIZE));
    }

    return Promise.all(
      groupedProductCodes.map(group =>
        http.get(
          join(
            api2,
            // eslint-disable-next-line max-len
            `/products/availability?productCodes=${group}&accountNumber=${accountNumber}&details=true&realTime=${realTime}&departmentNumber=${departmentNumber}`
          )
        )
      )
    )
      .then(responses => {
        const availabilities = responses.reduce(
          (accumulator, { data: { availabilities, futureAvailabilities } }) => {
            accumulator.currentAvailabilities.push(...availabilities);
            if (futureAvailabilities) {
              accumulator.futureAvailabilities.push(...futureAvailabilities);
            }
            return accumulator;
          },
          { currentAvailabilities: [], futureAvailabilities: [] }
        );
        const success = action.success(availabilities);

        dispatch(success);
        dispatch(validateCart());

        return success;
      })
      .catch(error => dispatch(action.error(error)));
  }
});

export const requestMinRules = asyncAction({
  baseName: 'REQUEST_MIN_RULES_LEGACY',
  action: action => () => dispatch => {
    dispatch(action.start());
    return http
      .get(join(import.meta.env.VITE_MIN_MAX_URL, 'min/rules/v1'))
      .then(({ data: response }) => dispatch(action.success(response)))
      .catch(error => dispatch(action.error({ error })));
  }
});

export const toggleIsAdminCart = createAction('TOGGLE_IS_ADMIN_CART_LEGACY');
export const setAdminCartId = createAction('SET_ADMIN_CART_ID_LEGACY');

export const setIsShopping = createAction('SET_IS_SHOPPING_LEGACY');

export const clearSelectedDepartment = createAction('CLEAR_SELECTED_DEPARTMENT_LEGACY');
